import React, { useEffect, useMemo, useRef, useState } from "react";

export type LakeMapSwim = {
  id: string;
  name: string;
  source?: string;
  latitude?: number | null;
  longitude?: number | null;
  accuracy_m?: number | null;
  captured_at?: string | null;
  can_edit_personal?: boolean;
  can_edit_shared?: boolean;
};

type Props = {
  latitude: number | null;
  longitude: number | null;
  swims: LakeMapSwim[];
  highlightName?: string | null;
  compact?: boolean;
};

const OSM_RASTER_STYLE = {
  version: 8 as const,
  sources: {
    osm: {
      type: "raster" as const,
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [
    {
      id: "osm",
      type: "raster" as const,
      source: "osm",
    },
  ],
};

function validCoordinate(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function sourceLabel(source?: string) {
  if (source === "verified_owner" || source === "verified_community")
    return "Verified lake peg";
  if (source === "verified_seed") return "Known venue peg";
  if (source === "pending_review") return "Private · awaiting review";
  if (source === "review_rejected") return "Private";
  if (source === "private") return "Your private peg";
  return "Known peg";
}

export function InteractiveLakeMap({
  latitude,
  longitude,
  swims,
  highlightName,
  compact = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<import("maplibre-gl").Map | null>(null);
  const markerRefs = useRef<import("maplibre-gl").Marker[]>([]);
  const [loadError, setLoadError] = useState("");
  const [mapReady, setMapReady] = useState(false);

  const mappedSwims = useMemo(
    () =>
      swims.filter(
        (swim) =>
          validCoordinate(swim.latitude) && validCoordinate(swim.longitude),
      ),
    [swims],
  );

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let cancelled = false;

    void (async () => {
      try {
        const maplibre = await import("maplibre-gl");
        await import("maplibre-gl/dist/maplibre-gl.css");
        if (cancelled || !containerRef.current) return;

        const centre: [number, number] =
          validCoordinate(latitude) && validCoordinate(longitude)
            ? [longitude, latitude]
            : [-1.5, 52.4];

        const map = new maplibre.Map({
          container: containerRef.current,
          style: OSM_RASTER_STYLE,
          center: centre,
          zoom:
            validCoordinate(latitude) && validCoordinate(longitude) ? 15 : 5.2,
          attributionControl: false,
          touchPitch: false,
        });

        map.addControl(
          new maplibre.NavigationControl({ showCompass: false }),
          "top-right",
        );
        map.on("error", () => {
          if (!cancelled)
            setLoadError(
              "Some map tiles could not load. Peg information is still available below.",
            );
        });
        mapRef.current = map;
        map.once("load", () => {
          if (!cancelled) setMapReady(true);
        });
      } catch {
        if (!cancelled)
          setLoadError(
            "The interactive map could not load. Peg information is still available below.",
          );
      }
    })();

    return () => {
      cancelled = true;
      markerRefs.current.forEach((marker) => marker.remove());
      markerRefs.current = [];
      mapRef.current?.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    void import("maplibre-gl").then((maplibre) => {
      if (!mapRef.current) return;

      markerRefs.current.forEach((marker) => marker.remove());
      markerRefs.current = [];

      const bounds = new maplibre.LngLatBounds();
      let highlightedPoint: [number, number] | null = null;

      for (const swim of mappedSwims) {
        if (
          !validCoordinate(swim.latitude) ||
          !validCoordinate(swim.longitude)
        )
          continue;

        const highlighted =
          Boolean(highlightName) &&
          swim.name.trim().toLowerCase() ===
            String(highlightName).trim().toLowerCase();
        const shared =
          swim.source === "verified_owner" ||
          swim.source === "verified_community";

        const element = document.createElement("button");
        element.type = "button";
        element.className = highlighted
          ? "flex min-h-8 items-center gap-1 rounded-full border-2 border-white bg-accent px-2 py-1 text-xs font-bold text-white shadow-lg"
          : shared
            ? "flex min-h-8 items-center gap-1 rounded-full border border-white bg-primary px-2 py-1 text-xs font-bold text-background shadow-md"
            : "flex min-h-8 items-center gap-1 rounded-full border border-accent bg-raised px-2 py-1 text-xs font-semibold text-primary shadow-md";

        const dot = document.createElement("span");
        dot.textContent = highlighted ? "●" : "•";
        const name = document.createElement("span");
        name.textContent = swim.name;
        element.append(dot, name);

        const popupContent = document.createElement("div");
        popupContent.className = "min-w-[180px] space-y-1.5";
        const popupName = document.createElement("strong");
        popupName.textContent = swim.name;
        popupName.className = "block text-sm font-semibold text-primary";
        popupContent.appendChild(popupName);
        const popupSource = document.createElement("div");
        popupSource.textContent = sourceLabel(swim.source);
        popupSource.className = shared
          ? "text-xs font-semibold text-success"
          : "text-xs font-semibold text-accent";
        popupContent.appendChild(popupSource);
        if (validCoordinate(swim.accuracy_m)) {
          const accuracy = document.createElement("div");
          accuracy.textContent = `GPS accuracy about ${Math.round(swim.accuracy_m)} m`;
          accuracy.className = "text-xs text-secondary";
          popupContent.appendChild(accuracy);
        }

        const popup = new maplibre.Popup({
          offset: 20,
          closeButton: false,
          className: "catchcompass-map-popup",
        }).setDOMContent(popupContent);

        const point: [number, number] = [swim.longitude, swim.latitude];
        const marker = new maplibre.Marker({
          element,
          anchor: "bottom",
        })
          .setLngLat(point)
          .setPopup(popup)
          .addTo(mapRef.current);

        markerRefs.current.push(marker);
        bounds.extend(point);
        if (highlighted) {
          highlightedPoint = point;
          popup.addTo(mapRef.current);
        }
      }

      if (highlightedPoint) {
        mapRef.current.easeTo({
          center: highlightedPoint,
          zoom: Math.max(mapRef.current.getZoom(), 16),
          duration: 450,
        });
      } else if (mappedSwims.length > 1 && !bounds.isEmpty()) {
        mapRef.current.fitBounds(bounds, {
          padding: compact ? 36 : 56,
          maxZoom: 17,
          duration: 450,
        });
      } else if (mappedSwims.length === 1) {
        const swim = mappedSwims[0];
        mapRef.current.easeTo({
          center: [Number(swim.longitude), Number(swim.latitude)],
          zoom: Math.max(mapRef.current.getZoom(), 16),
          duration: 450,
        });
      } else if (validCoordinate(latitude) && validCoordinate(longitude)) {
        mapRef.current.easeTo({
          center: [longitude, latitude],
          zoom: Math.max(mapRef.current.getZoom(), 15),
          duration: 450,
        });
      }
    });
  }, [mapReady, mappedSwims, highlightName, latitude, longitude, compact]);

  return (
    <div className="space-y-2">
      <div
        ref={containerRef}
        className={`${compact ? "h-[280px]" : "h-[430px]"} w-full overflow-hidden rounded-xl border border-border bg-inset`}
        aria-label="Interactive CatchCompass lake map"
      />
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-secondary">
        <span>• Verified/shared peg</span>
        <span className="text-accent">• Your private peg</span>
        {highlightName ? <span className="font-semibold">Highlighted: {highlightName}</span> : null}
      </div>
      {loadError ? (
        <p className="text-sm text-warning" role="status">
          {loadError}
        </p>
      ) : null}
      <p className="text-xs text-secondary">
        Map data © OpenStreetMap contributors. Exact private peg GPS remains visible only to your account unless the peg is explicitly approved as shared.
      </p>
    </div>
  );
}
